import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type CSSProperties, type FormEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type SelectHTMLAttributes } from "react";
import { errorMessage, errorReference } from "../lib/errors";
import { formatMoney, type CurrencyLike } from "../lib/money";
import { CONTACT_EMAIL, contactMailto } from "../lib/site";

// ---------- buttons & fields ----------

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "danger" | "danger-solid" | "ghost"; size?: "small"; busy?: boolean };

export function Button({ variant, size, busy, className, children, disabled, type = "button", ...rest }: BtnProps) {
  const cls = ["btn", variant === "danger-solid" ? "danger solid" : variant, size, className].filter(Boolean).join(" ");
  return (
    <button type={type} className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? "Working…" : children}
    </button>
  );
}

/**
 * A small "i" that explains a field on hover or keyboard focus, so forms can
 * keep details out of the way. The text is linked to the button with
 * aria-describedby, so screen readers announce it too.
 */
export function InfoTip({ children, label = "More information", align }: { children: ReactNode; label?: string; align?: "start" | "end" }) {
  const id = useId();
  // Open towards whichever side has room, so a tip near the right edge never runs off screen,
  // and upwards when the scrolling box it sits in (a dialog's body, or the page) has no room below.
  const [side, setSide] = useState<"start" | "end">(align ?? "start");
  const [up, setUp] = useState(false);
  const place = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    if (!align) setSide(r.left > window.innerWidth / 2 ? "end" : "start");
    const box = el.closest(".modal-body");
    const floor = box ? box.getBoundingClientRect().bottom : window.innerHeight;
    setUp(floor - r.bottom < 150 && r.top - (box ? box.getBoundingClientRect().top : 0) > 150);
  };
  return (
    <span className={`tip ${side}${up ? " up" : ""}`} onPointerEnter={(e) => place(e.currentTarget)} onFocus={(e) => place(e.currentTarget)}>
      <button type="button" className="tip-btn" aria-label={label} aria-describedby={id} onClick={(e) => e.preventDefault()}>i</button>
      <span role="tooltip" id={id} className="tip-body">{children}</span>
    </span>
  );
}

export function Field({ label, hint, tip, tipAlign, children }: { label: string; hint?: ReactNode; tip?: ReactNode; tipAlign?: "start" | "end"; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}{tip && <InfoTip label={`About ${label.toLowerCase()}`} align={tipAlign}>{tip}</InfoTip>}</span>
      {children}
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

/** An input with a fixed marker inside it, e.g. a currency symbol before or "%" after. */
export function Affix({ before, after, children }: { before?: string; after?: string; children: ReactNode }) {
  const pad = (s?: string) => (s ? `${Math.max(1, s.length) * 0.62 + 1.4}em` : undefined);
  return (
    <div className="affix" style={{ "--affix-before": pad(before), "--affix-after": pad(after) } as CSSProperties}>
      {before && <span className="affix-before" aria-hidden="true">{before}</span>}
      {children}
      {after && <span className="affix-after" aria-hidden="true">{after}</span>}
    </div>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={className ? `input ${className}` : "input"} {...props} />;
}

export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className="select input" {...props}>
      {children}
    </select>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------- surfaces ----------

export function Panel({ title, sub, actions, children, flush }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel-head">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <p className="muted small">{sub}</p>}
          </div>
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {flush ? children : <div className="panel-body">{children}</div>}
    </section>
  );
}

export function PageHead({ title, lede, actions, badge }: { title: ReactNode; lede?: ReactNode; actions?: ReactNode; badge?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <div className="row">
          <h1>{title}</h1>
          {badge}
        </div>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 13l3-8h12l3 8v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM3 13h5l1 2h6l1-2h5" />
        </svg>
      </span>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <p className="loading" role="status">
      {label}…
    </p>
  );
}

// ---------- status ----------

export function StatusBadge({ status }: { status: string }) {
  const tone = status === "active" || status === "posted" || status === "completed" || status === "captured" ? "ok"
    : status === "suspended" || status === "frozen" || status === "closed" || status === "deleted" || status === "revoked" || status === "failed" || status === "expired" || status === "reversed" ? "bad"
    : "";
  return <span className={`badge ${tone}`}>{status}</span>;
}

/** A deleted game is stored as "closed"; show it the way developers and admins think of it. */
export function GameStatusBadge({ status }: { status: string }) {
  return <StatusBadge status={status === "closed" ? "deleted" : status} />;
}

export function EnvBadge({ env }: { env: string }) {
  return <span className={`badge ${env}`}>{env}</span>;
}

export function Money({ minor, currency, signed, tone }: { minor: number; currency: CurrencyLike; signed?: boolean; tone?: "credit" | "debit" }) {
  return <span className={`amt ${tone ?? ""}`}>{formatMoney(minor, currency, { sign: signed })}</span>;
}

// ---------- errors ----------

/**
 * An error in plain words. The engine's code and request ID sit behind a
 * collapsed "Details", ready to copy for support, never in the headline.
 */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const { code, requestId } = errorReference(error);
  return (
    <div className="notice error" role="alert">
      <div className="row">
        <span className="grow">{errorMessage(error)}</span>
        {onRetry && (
          <Button size="small" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
      {requestId && (
        <details className="error-details">
          <summary>Details</summary>
          <div className="error-ref">
            <span>
              {code && <>Error <code>{code}</code> · </>}
              Reference <code>{requestId}</code>
            </span>
            <CopyButton text={requestId} label="Copy reference" />
          </div>
          <p>
            Contacting support at{" "}
            <a href={contactMailto("Game Banking Engine support")}>{CONTACT_EMAIL}</a>
            ? Include the reference so we can find exactly what happened.
          </p>
        </details>
      )}
    </div>
  );
}

// ---------- copy & secrets ----------

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 1600);
    } catch {
      /* clipboard blocked: the value is visible and selectable */
    }
  };
  return (
    <Button size="small" onClick={copy}>
      {done ? "Copied" : label}
    </Button>
  );
}

/** Shows a secret that the engine will never show again. */
export function SecretReveal({ secret, what, children }: { secret: string; what: string; children?: ReactNode }) {
  return (
    <div className="secret" role="status">
      <strong>Copy this {what} now. It won't be shown again.</strong>
      <code data-testid="secret">{secret}</code>
      <div className="row">
        <CopyButton text={secret} label={`Copy ${what}`} />
        {children}
      </div>
    </div>
  );
}

// ---------- action menu ----------

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

/**
 * A "⋯" button that opens a small menu of row actions. The menu is fixed-
 * positioned so a table's scroll container can't clip it, and it closes on
 * Escape, an outside click, scrolling or resizing.
 */
export function ActionMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const open = pos !== null;

  const close = useCallback((refocus = false) => {
    setPos(null);
    if (refocus) btn.current?.focus();
  }, []);

  const toggle = () => {
    if (open) return close();
    const r = btn.current!.getBoundingClientRect();
    const height = items.length * 36 + 12; // rough; only decides whether to open upwards
    const up = r.bottom + height > window.innerHeight && r.top > height;
    setPos({ right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !btn.current?.contains(t)) close();
    };
    const dismiss = () => close();
    document.addEventListener("pointerdown", outside);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open, close]);

  const onKeyDown = (e: KeyboardEvent) => {
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") { e.preventDefault(); close(true); }
    else if (e.key === "Tab") close();
    else if (e.key === "ArrowDown") { e.preventDefault(); all[(i + 1) % all.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); all[(i - 1 + all.length) % all.length]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); all[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); all[all.length - 1]?.focus(); }
  };

  return (
    <>
      <button ref={btn} type="button" className="btn ghost small icon-only" aria-label={label} aria-haspopup="menu" aria-expanded={open}
        aria-controls={open ? id : undefined} onClick={toggle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
      </button>
      {open && (
        <div ref={menu} id={id} role="menu" aria-label={label} className="action-menu" style={pos} onKeyDown={onKeyDown}>
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" tabIndex={-1} className={it.danger ? "danger" : undefined}
              onClick={() => { close(); it.onSelect(); }}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- modal ----------

export function Modal({ open, title, description, onClose, children, footer, onSubmit, size }: {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  onSubmit?: (e: FormEvent) => void;
  /** "wide" for longer forms. */
  size?: "wide";
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
      // showModal focuses the first focusable element; never start on a field's tooltip button.
      if (document.activeElement instanceof HTMLElement && document.activeElement.classList.contains("tip-btn")) {
        d.querySelector<HTMLElement>("input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(.tip-btn):not(:disabled)")?.focus();
      }
    }
    if (!open && d.open) d.close();
  }, [open]);
  const body = (
    <>
      <header>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </header>
      <div className="modal-body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </>
  );
  return (
    <dialog ref={ref} className={`modal${size ? ` ${size}` : ""}`} onClose={onClose} onCancel={onClose} aria-label={title}>
      {open &&
        (onSubmit ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(e);
            }}
          >
            {body}
          </form>
        ) : (
          body
        ))}
    </dialog>
  );
}

/** A confirmation step for consequential actions. */
export function Confirm({ open, title, body, confirmLabel, danger, busy, disabled, error, onConfirm, onClose, children }: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  /** Keeps the confirm button off until the dialog's own input is satisfied. */
  disabled?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={onConfirm}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant={danger ? "danger-solid" : "primary"} busy={busy} disabled={disabled}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div>{body}</div>
        {children}
        <ErrorNotice error={error} />
      </div>
    </Modal>
  );
}

// ---------- toasts ----------

interface Toast {
  id: number;
  text: string;
  tone?: "error";
}
const ToastCtx = createContext<(text: string, tone?: "error") => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone?: "error") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }].slice(-3));
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone ?? ""}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

// ---------- pagination ----------

export function Pager({ total, limit, offset, onChange }: { total: number; limit: number; offset: number; onChange: (offset: number) => void }) {
  if (total <= limit) return null;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="pager">
      <span>
        {from}–{to} of {total}
      </span>
      <Button size="small" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        Previous
      </Button>
      <Button size="small" disabled={to >= total} onClick={() => onChange(offset + limit)}>
        Next
      </Button>
    </div>
  );
}
